import React, { useState } from 'react'

import { pantryColors, pantryShadows } from '../../../foundations/tokens'

interface ToggleProps {
  checked?: boolean
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  label?: string
  description?: string
  disabled?: boolean
  size?: 'sm' | 'md'
}

export const Toggle: React.FC<ToggleProps> = ({
  checked,
  defaultChecked = false,
  onChange,
  label,
  description,
  disabled = false,
  size = 'md',
}) => {
  const [internalChecked, setInternalChecked] = useState(defaultChecked)
  const isControlled = checked !== undefined
  const isOn = isControlled ? checked : internalChecked

  const trackSize =
    size === 'sm'
      ? { width: 32, height: 18, borderRadius: 9 }
      : { width: 40, height: 22, borderRadius: 11 }

  const knobSize =
    size === 'sm'
      ? { size: 12, offset: 3, travel: 14 }
      : { size: 16, offset: 3, travel: 18 }

  const handleToggle = () => {
    if (disabled) return
    const next = !isOn
    if (!isControlled) setInternalChecked(next)
    onChange?.(next)
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      {(label || description) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {label && (
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: pantryColors.gray[700],
              }}
            >
              {label}
            </span>
          )}
          {description && (
            <span style={{ fontSize: 11.5, color: pantryColors.gray[400] }}>
              {description}
            </span>
          )}
        </div>
      )}

      <button
        type="button"
        role="switch"
        aria-checked={isOn}
        onClick={handleToggle}
        disabled={disabled}
        style={{
          ...trackSize,
          background: isOn
            ? pantryColors.brand.primary
            : pantryColors.gray[300],
          cursor: disabled ? 'not-allowed' : 'pointer',
          position: 'relative',
          border: 'none',
          flexShrink: 0,
          transition: 'background 0.2s',
          opacity: disabled ? 0.5 : 1,
          padding: 0,
        }}
      >
        <span
          style={{
            width: knobSize.size,
            height: knobSize.size,
            borderRadius: '50%',
            background: pantryColors.white,
            position: 'absolute',
            top: knobSize.offset,
            left: knobSize.offset,
            transform: isOn
              ? `translateX(${knobSize.travel}px)`
              : 'translateX(0)',
            transition: 'transform 0.2s',
            boxShadow: pantryShadows.sm,
            display: 'block',
          }}
        />
      </button>
    </div>
  )
}
